"use client";

/**
 * Wide diagnostics buffer.
 *
 * The existing client-diagnostics pipeline only reports *errors* (failed API
 * calls, console.error, crashes). That means a page that is merely slow or
 * laggy — but not throwing — produces zero diagnostic events, so an agent
 * looking afterwards sees nothing.
 *
 * This module keeps a continuous, generous ring buffer of EVERYTHING happening
 * in the page (all console output, every network call with timing, long tasks,
 * laggy interactions, layout shifts, web-vitals, route changes) since the page
 * loaded. On a manual keystroke we flush the whole buffer plus point-in-time
 * snapshots (resource timing waterfall, navigation timing, memory) as a single
 * rich "manual_capture" event the agent can pull back in full.
 *
 * The window is intentionally WIDE: we keep up to ~10 minutes / generous item
 * counts so the lead-up to "this is the lag I mean" is always present.
 */

import { reportClientDiagnostic } from "@/utils/client-diagnostics";

type ConsoleLevel = "log" | "info" | "warn" | "error" | "debug";

type ConsoleEntry = {
  t: number; // ms since page nav start
  level: ConsoleLevel;
  message: string;
};

type NetworkEntry = {
  t: number;
  method: string;
  url: string;
  status: number | null;
  ok: boolean | null;
  durationMs: number;
  kind: "api" | "same-origin" | "external";
};

type LongTaskEntry = {
  t: number;
  durationMs: number;
  attribution?: string;
};

type InteractionEntry = {
  t: number;
  name: string; // event type, e.g. "click", "keydown"
  durationMs: number;
  target?: string;
};

type RouteEntry = {
  t: number;
  route: string;
};

// Generous caps — "wide" by design. These are ring buffers (oldest dropped).
const CAP_CONSOLE = 800;
const CAP_NETWORK = 500;
const CAP_LONGTASK = 300;
const CAP_INTERACTION = 300;
const CAP_ROUTE = 100;
const MAX_AGE_MS = 10 * 60 * 1000; // keep ~10 minutes of history

declare global {
  interface Window {
    __TM_DIAG_BUFFER__?: DiagnosticsBuffer;
    __TM_DIAG_BUFFER_INSTALLED__?: boolean;
  }
}

type DiagnosticsBuffer = {
  console: ConsoleEntry[];
  network: NetworkEntry[];
  longTasks: LongTaskEntry[];
  interactions: InteractionEntry[];
  routes: RouteEntry[];
  vitals: {
    lcpMs: number | null;
    fcpMs: number | null;
    ttfbMs: number | null;
    cls: number; // cumulative
    inpMs: number | null; // worst interaction so far
    layoutShiftCount: number;
  };
  navStart: number;
};

function now(): number {
  // ms since the page started loading
  return typeof performance !== "undefined" ? performance.now() : 0;
}

function getBuffer(): DiagnosticsBuffer | null {
  if (typeof window === "undefined") return null;
  if (!window.__TM_DIAG_BUFFER__) {
    window.__TM_DIAG_BUFFER__ = {
      console: [],
      network: [],
      longTasks: [],
      interactions: [],
      routes: [],
      vitals: {
        lcpMs: null,
        fcpMs: null,
        ttfbMs: null,
        cls: 0,
        inpMs: null,
        layoutShiftCount: 0,
      },
      navStart: now(),
    };
  }
  return window.__TM_DIAG_BUFFER__;
}

function pushCapped<T extends { t: number }>(arr: T[], item: T, cap: number) {
  arr.push(item);
  // Drop by count
  if (arr.length > cap) arr.splice(0, arr.length - cap);
  // Drop by age (keep buffer from holding stale history on long-lived tabs)
  const cutoff = now() - MAX_AGE_MS;
  while (arr.length && arr[0].t < cutoff) arr.shift();
}

function clip(value: unknown, max = 2000): string {
  let text: string;
  if (typeof value === "string") text = value;
  else if (value instanceof Error) text = `${value.name}: ${value.message}`;
  else {
    try {
      text = JSON.stringify(value);
    } catch {
      text = String(value);
    }
  }
  return text.length > max ? `${text.slice(0, max)}…` : text;
}

// ---- Recording entry points (called from console wrap / fetch wrap) ----

export function recordConsole(level: ConsoleLevel, args: unknown[]) {
  const buf = getBuffer();
  if (!buf) return;
  const message = args.map((a) => clip(a, 600)).join(" ");
  pushCapped(buf.console, { t: now(), level, message }, CAP_CONSOLE);
}

export function recordNetwork(entry: Omit<NetworkEntry, "t">) {
  const buf = getBuffer();
  if (!buf) return;
  pushCapped(buf.network, { t: now(), ...entry }, CAP_NETWORK);
}

export function recordRoute(route: string) {
  const buf = getBuffer();
  if (!buf) return;
  const last = buf.routes[buf.routes.length - 1];
  if (last && last.route === route) return;
  pushCapped(buf.routes, { t: now(), route }, CAP_ROUTE);
}

// ---- Install: console wrapping + performance observers ----

function describeNode(node: unknown): string | undefined {
  const el = node as Element | null;
  if (!el || typeof el !== "object" || !("tagName" in el)) return undefined;
  const tag = (el.tagName || "").toLowerCase();
  const id = el.id ? `#${el.id}` : "";
  const cls =
    typeof el.className === "string" && el.className
      ? `.${el.className.trim().split(/\s+/).slice(0, 2).join(".")}`
      : "";
  return `${tag}${id}${cls}`.slice(0, 80);
}

export function installDiagnosticsBuffer() {
  if (typeof window === "undefined") return;
  if (window.__TM_DIAG_BUFFER_INSTALLED__) return;
  window.__TM_DIAG_BUFFER_INSTALLED__ = true;
  getBuffer();

  // Keep a large resource-timing waterfall available for the snapshot.
  try {
    performance.setResourceTimingBufferSize?.(600);
  } catch {
    /* noop */
  }

  // Wrap console.log/info/warn/debug (console.error is captured separately by
  // the existing error-handler, which also calls recordConsole).
  const levels: ConsoleLevel[] = ["log", "info", "warn", "debug"];
  for (const level of levels) {
    const original = console[level]?.bind(console);
    if (!original) continue;
    console[level] = (...args: unknown[]) => {
      try {
        recordConsole(level, args);
      } catch {
        /* never let logging break */
      }
      original(...args);
    };
  }

  // SPA route changes via history API.
  try {
    recordRoute(`${location.pathname}${location.search}`);
    const wrap = (key: "pushState" | "replaceState") => {
      const orig = history[key].bind(history);
      history[key] = (...args: Parameters<History["pushState"]>) => {
        const ret = orig(...args);
        try {
          recordRoute(`${location.pathname}${location.search}`);
        } catch {
          /* noop */
        }
        return ret;
      };
    };
    wrap("pushState");
    wrap("replaceState");
    window.addEventListener("popstate", () =>
      recordRoute(`${location.pathname}${location.search}`),
    );
  } catch {
    /* noop */
  }

  const buf = getBuffer();
  if (!buf) return;

  const observe = (
    type: string,
    cb: (entry: PerformanceEntry) => void,
    opts?: PerformanceObserverInit,
  ) => {
    try {
      const obs = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) cb(e);
      });
      obs.observe({ type, buffered: true, ...opts });
    } catch {
      /* entry type unsupported in this browser */
    }
  };

  // Long tasks — the main-thread freezes that cause jank.
  observe("longtask", (e) => {
    const attrib = (e as PerformanceEntry & {
      attribution?: Array<{ name?: string; containerName?: string }>;
    }).attribution?.[0];
    pushCapped(
      buf.longTasks,
      {
        t: e.startTime,
        durationMs: Math.round(e.duration),
        attribution: attrib?.containerName || attrib?.name,
      },
      CAP_LONGTASK,
    );
  });

  // Slow interactions (INP-style) — laggy clicks/keystrokes.
  observe(
    "event",
    (e) => {
      const evt = e as PerformanceEventTiming;
      const durationMs = Math.round(evt.duration);
      if (durationMs < 40) return; // only keep the laggy ones
      pushCapped(
        buf.interactions,
        {
          t: evt.startTime,
          name: evt.name,
          durationMs,
          target: describeNode((evt as PerformanceEventTiming & { target?: Node }).target),
        },
        CAP_INTERACTION,
      );
      if (buf.vitals.inpMs == null || durationMs > buf.vitals.inpMs) {
        buf.vitals.inpMs = durationMs;
      }
    },
    { durationThreshold: 40 } as PerformanceObserverInit,
  );

  // Largest Contentful Paint.
  observe("largest-contentful-paint", (e) => {
    buf.vitals.lcpMs = Math.round(e.startTime);
  });

  // First Contentful Paint.
  observe("paint", (e) => {
    if (e.name === "first-contentful-paint") {
      buf.vitals.fcpMs = Math.round(e.startTime);
    }
  });

  // Cumulative Layout Shift.
  observe("layout-shift", (e) => {
    const ls = e as PerformanceEntry & { value: number; hadRecentInput: boolean };
    if (ls.hadRecentInput) return;
    buf.vitals.cls += ls.value;
    buf.vitals.layoutShiftCount += 1;
  });

  // TTFB from navigation timing.
  try {
    const nav = performance.getEntriesByType(
      "navigation",
    )[0] as PerformanceNavigationTiming | undefined;
    if (nav) buf.vitals.ttfbMs = Math.round(nav.responseStart);
  } catch {
    /* noop */
  }
}

// ---- Snapshot helpers (point-in-time, gathered at capture) ----

function resourceTimingSnapshot() {
  try {
    const entries = performance.getEntriesByType(
      "resource",
    ) as PerformanceResourceTiming[];
    return entries
      .map((r) => ({
        name: r.name.length > 200 ? `${r.name.slice(0, 200)}…` : r.name,
        type: r.initiatorType,
        startMs: Math.round(r.startTime),
        durationMs: Math.round(r.duration),
        transferKb:
          typeof r.transferSize === "number"
            ? Math.round(r.transferSize / 1024)
            : null,
      }))
      .sort((a, b) => b.durationMs - a.durationMs)
      .slice(0, 80); // slowest 80 resources
  } catch {
    return [];
  }
}

function navigationTimingSnapshot() {
  try {
    const nav = performance.getEntriesByType(
      "navigation",
    )[0] as PerformanceNavigationTiming | undefined;
    if (!nav) return null;
    return {
      ttfbMs: Math.round(nav.responseStart),
      domInteractiveMs: Math.round(nav.domInteractive),
      domContentLoadedMs: Math.round(nav.domContentLoadedEventEnd),
      loadEventMs: Math.round(nav.loadEventEnd),
      transferKb:
        typeof nav.transferSize === "number"
          ? Math.round(nav.transferSize / 1024)
          : null,
      type: nav.type,
    };
  } catch {
    return null;
  }
}

function environmentSnapshot() {
  const conn = (navigator as Navigator & {
    connection?: { effectiveType?: string; downlink?: number; rtt?: number };
    deviceMemory?: number;
  }).connection;
  const mem = (performance as Performance & {
    memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };
  }).memory;
  return {
    url: location.href,
    route: `${location.pathname}${location.search}`,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    dpr: window.devicePixelRatio,
    hardwareConcurrency: navigator.hardwareConcurrency ?? null,
    deviceMemoryGb:
      (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null,
    connection: conn
      ? { effectiveType: conn.effectiveType, downlink: conn.downlink, rtt: conn.rtt }
      : null,
    jsHeapMb: mem ? Math.round(mem.usedJSHeapSize / 1048576) : null,
    jsHeapLimitMb: mem ? Math.round(mem.jsHeapSizeLimit / 1048576) : null,
  };
}

/**
 * Flush the whole buffer + snapshots as one rich diagnostic event.
 * Returns the human-friendly tag so the UI can show it (and the user can
 * mention it), or null if it couldn't run.
 */
export async function captureDiagnosticBundle(
  note?: string,
): Promise<{ tag: string } | null> {
  if (typeof window === "undefined") return null;
  const buf = getBuffer();
  if (!buf) return null;

  // Short, human-friendly tag derived from time + route (no Date.now reliance
  // for readability — uses wall clock here, which is fine in the browser).
  const stamp = new Date();
  const hh = String(stamp.getHours()).padStart(2, "0");
  const mm = String(stamp.getMinutes()).padStart(2, "0");
  const ss = String(stamp.getSeconds()).padStart(2, "0");
  const routeSlug =
    location.pathname.replace(/\//g, "-").replace(/^-/, "").slice(0, 40) ||
    "root";
  const tag = `cap-${routeSlug}-${hh}${mm}${ss}`;

  const env = environmentSnapshot();

  const bundle = {
    tag,
    note: note ?? null,
    capturedAtLocal: stamp.toString(),
    sincePageLoadMs: Math.round(now() - buf.navStart),
    environment: env,
    vitals: buf.vitals,
    counts: {
      console: buf.console.length,
      network: buf.network.length,
      longTasks: buf.longTasks.length,
      slowInteractions: buf.interactions.length,
      routeChanges: buf.routes.length,
    },
    navigationTiming: navigationTimingSnapshot(),
    longTasks: buf.longTasks.slice(-CAP_LONGTASK),
    slowInteractions: buf.interactions.slice(-CAP_INTERACTION),
    routeHistory: buf.routes.slice(-CAP_ROUTE),
    network: buf.network.slice(-CAP_NETWORK),
    console: buf.console.slice(-CAP_CONSOLE),
    slowestResources: resourceTimingSnapshot(),
  };

  // Use the existing client-event pipeline, but force a real fetch POST
  // (not sendBeacon) so the large bundle isn't dropped by beacon size limits.
  reportClientDiagnostic({
    eventType: "manual_capture",
    severity: "info",
    source: "browser-capture",
    message: note ? `Manual capture: ${note}` : `Manual capture @ ${env.route}`,
    details: bundle as unknown as Record<string, unknown>,
    forceFetch: true,
  });

  return { tag };
}
